import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { StreamedReplyProgressService } from './streamed-reply-progress.service';

@Component({
  selector: 'fastr-ui-streaming-text',
  template: '{{ shown }}',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StreamingTextComponent implements OnInit, OnDestroy {
  @Input() set text(value: string | null) {
    this.target = value ?? '';
    this.schedule();
  }

  @Input() set complete(value: boolean | null) {
    this.finished = value ?? false;
    this.schedule();
  }

  /** The live reply bubble: hands what it shows to the message that replaces it at commit. */
  @Input() publishProgress = false;

  /** Start from what the live bubble had already shown instead of from empty. */
  @Input() continueFromStream = false;

  // A frame made the text taller. Emitted outside the zone on purpose: the host only scrolls,
  // so the typewriter still costs no application-wide change detection.
  @Output() grew = new EventEmitter<void>();

  /** Emitted once, inside the zone, when the whole text is on screen. */
  @Output() typed = new EventEmitter<void>();

  shown = '';

  private readonly BACKLOG_DRAIN_SECONDS = 0.55;
  private readonly MIN_CHARS_PER_SECOND = 26;
  private readonly MAX_CHARS_PER_SECOND = 130;
  private readonly FINISHED_DRAIN_SECONDS = 0.3;
  private readonly FINISHED_MAX_CHARS_PER_SECOND = 280;
  private readonly ERASE_DRAIN_SECONDS = 0.22;
  private readonly ERASE_MIN_CHARS_PER_SECOND = 70;
  private readonly ERASE_MAX_CHARS_PER_SECOND = 340;

  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly progress = inject(StreamedReplyProgressService);
  private target = '';
  private frame: number | null = null;
  private lastFrameAt = 0;
  private finished = false;
  private destroyed = false;
  private ready = false;
  private announced = false;
  private announceFrame: number | null = null;
  private readonly reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Seeding waits for OnInit so it runs once every input is bound, whatever their order.
  ngOnInit() {
    if (this.publishProgress) {
      this.progress.publish('');
      return;
    }
    if (this.continueFromStream) {
      this.seed(this.progress.take());
    }
    this.ready = true;
    this.schedule();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.report();
    this.cancel();
    if (this.announceFrame !== null) {
      cancelAnimationFrame(this.announceFrame);
    }
  }

  /** Resume at what the recruiter already watched; the remainder drains as settled text. */
  private seed(shownSoFar: string) {
    const keep = commonPrefixLength(shownSoFar, this.target);
    if (!keep) {
      return;
    }
    this.shown = this.target.slice(0, keep);
    this.finished = true;
    this.schedule();
  }

  private schedule() {
    if (this.destroyed || this.frame !== null) {
      return;
    }
    if (this.shown === this.target) {
      this.announce();
      return;
    }
    // Reduced motion: no frames at all. Every caller is an input setter or ngOnInit, so the
    // OnPush view is already due a check and renders the text in this same pass.
    if (this.reducedMotion) {
      this.shown = this.target;
      this.report();
      this.announce();
      return;
    }
    this.lastFrameAt = performance.now();
    this.zone.runOutsideAngular(() => {
      this.frame = requestAnimationFrame((now) => this.step(now));
    });
  }

  private step(now: number) {
    this.frame = null;
    if (this.destroyed) {
      return;
    }
    if (this.shown === this.target) {
      this.announce();
      return;
    }
    const elapsed = Math.max(0, (now - this.lastFrameAt) / 1000);
    this.lastFrameAt = now;
    this.shown = this.target.startsWith(this.shown) ? this.grown(elapsed) : this.erased(elapsed);
    this.report();
    this.cdr.detectChanges();
    this.grew.emit();
    this.schedule();
  }

  // Deferred a frame so a seed that already covers the text never emits mid change detection.
  private announce() {
    if (!this.ready || this.announced || this.publishProgress) {
      return;
    }
    this.announced = true;
    this.zone.runOutsideAngular(() => {
      this.announceFrame = requestAnimationFrame(() => {
        this.announceFrame = null;
        if (!this.destroyed) {
          this.zone.run(() => this.typed.emit());
        }
      });
    });
  }

  private report() {
    if (this.publishProgress) {
      this.progress.publish(this.shown);
    }
  }

  private grown(elapsed: number): string {
    const pending = this.target.length - this.shown.length;
    const window = this.finished ? this.FINISHED_DRAIN_SECONDS : this.BACKLOG_DRAIN_SECONDS;
    const ceiling = this.finished ? this.FINISHED_MAX_CHARS_PER_SECOND : this.MAX_CHARS_PER_SECOND;
    const rate = Math.min(ceiling, Math.max(this.MIN_CHARS_PER_SECOND, pending / window));
    return this.target.slice(0, this.shown.length + Math.max(1, Math.round(rate * elapsed)));
  }

  private erased(elapsed: number): string {
    const keep = commonPrefixLength(this.shown, this.target);
    const pending = this.shown.length - keep;
    const rate = Math.min(
      this.ERASE_MAX_CHARS_PER_SECOND,
      Math.max(this.ERASE_MIN_CHARS_PER_SECOND, pending / this.ERASE_DRAIN_SECONDS),
    );
    return this.shown.slice(
      0,
      Math.max(keep, this.shown.length - Math.max(1, Math.round(rate * elapsed))),
    );
  }

  private cancel() {
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }
}

const commonPrefixLength = (left: string, right: string): number => {
  const limit = Math.min(left.length, right.length);
  let i = 0;
  while (i < limit && left[i] === right[i]) {
    i += 1;
  }
  return i;
};
